<template>
    <div class="benchmark-cases" @mouseenter="isHovered = true" @mouseleave="isHovered = false"
        @focusin="isFocused = true" @focusout="handleFocusOut"
        @touchstart.passive="isTouching = true" @touchend="isTouching = false" @touchcancel="isTouching = false">
        <div v-if="hasOverflow" class="case-controls">
            <button type="button" aria-label="查看上一个案例" :disabled="atStart" @click="scrollCases(-1)">←</button>
            <button type="button" aria-label="查看下一个案例" :disabled="atEnd" @click="scrollCases(1)">→</button>
        </div>
        <div ref="track" class="case-track" tabindex="0" role="region" aria-label="标杆落地案例，可横向滚动"
            @scroll="updateScrollState">
            <article v-for="item in cases" :key="item.id" class="case-card">
                <img class="case-image" :class="{ 'image-contain': item.imageFit === 'contain' }"
                    :src="getImgPath(item.imageUrl)" :alt="item.name" loading="lazy">
                <h3>{{ item.name }}</h3>
                <p class="case-description">{{ item.text1 }}</p>
                <div class="case-organization">
                    <img :src="getImgPath(item.icon)" :alt="item.text2 + '标识'" loading="lazy">
                    <p>{{ item.text2 }}</p>
                </div>
            </article>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BenchmarkCases',
    props: {
        cases: { type: Array, required: true }
    },
    data() {
        return {
            hasOverflow: false,
            atStart: true,
            atEnd: false,
            resizeObserver: null,
            autoScrollTimer: null,
            isHovered: false,
            isFocused: false,
            isTouching: false
        };
    },
    mounted() {
        this.resizeObserver = new ResizeObserver(this.updateScrollState);
        this.resizeObserver.observe(this.$refs.track);
        this.updateScrollState();
        this.autoScrollTimer = window.setInterval(this.autoScroll, 3000);
    },
    beforeUnmount() {
        this.resizeObserver.disconnect();
        window.clearInterval(this.autoScrollTimer);
    },
    methods: {
        getImgPath(path) {
            return require('@/assets/' + path);
        },
        updateScrollState() {
            const track = this.$refs.track;
            this.hasOverflow = track.clientWidth > 0 && track.scrollWidth > track.clientWidth + 1;
            this.atStart = track.scrollLeft <= 1;
            this.atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        },
        handleFocusOut(event) {
            this.isFocused = this.$el.contains(event.relatedTarget);
        },
        autoScroll() {
            if (this.isHovered || this.isFocused || this.isTouching || document.hidden) return;
            this.updateScrollState();
            if (!this.hasOverflow) return;

            if (this.atEnd) {
                this.$refs.track.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
                this.scrollCases(1);
            }
        },
        scrollCases(direction) {
            const track = this.$refs.track;
            const cards = track.children;
            const distance = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth;
            track.scrollBy({ left: direction * distance, behavior: 'smooth' });
        }
    }
};
</script>

<style lang="scss" scoped>
.benchmark-cases {
    padding: 0 30px 70px;
}

.case-controls {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-bottom: 24px;

    button {
        width: 48px;
        height: 48px;
        background: #fff;
        border: 1px solid #313131;
        font-size: 24px;
        color: #313131;
        cursor: pointer;

        &:disabled { opacity: 0.3; cursor: default; }
    }
}

.case-track {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: calc((100% - 72px) / 4);
    grid-template-rows: auto auto 1fr auto;
    column-gap: 24px;
    overflow-x: auto;
    padding-bottom: 24px;
    scroll-snap-type: x mandatory;
    scrollbar-color: #666 #eee;
    scrollbar-width: auto;

    &:focus-visible { outline: 2px solid #313131; outline-offset: 4px; }
    &::-webkit-scrollbar { height: 8px; }
    &::-webkit-scrollbar-track { background: #eee; }
    &::-webkit-scrollbar-thumb { background: #666; border-radius: 4px; }
}

.case-card {
    grid-row: 1 / span 4;
    min-width: 0;
    padding: 20px;
    border: 2px solid #313131;
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    grid-template-rows: subgrid;
    row-gap: 0;
    scroll-snap-align: start;

    > * { min-width: 0; }

    .case-image {
        width: 100%;
        height: 180px;
        object-fit: cover;
        background: #f6f6f6;
    }

    .image-contain { object-fit: contain; }

    h3 { font-size: 24px; line-height: 1.5; margin: 24px 0 16px; overflow-wrap: anywhere; }
    p { margin: 0; }
    .case-description { font-size: 18px; line-height: 1.8; margin-bottom: 32px; }

    .case-organization {
        padding-top: 20px;
        border-top: 1px solid #ddd;
        display: flex;
        align-items: center;
        gap: 14px;
        font-size: 16px;
        line-height: 1.7;

        img { width: 64px; height: 56px; object-fit: contain; flex-shrink: 0; }
        p { min-width: 0; overflow-wrap: anywhere; }
    }
}

@supports not (grid-template-rows: subgrid) {
    .case-card {
        h3 { min-height: 3em; }
        .case-organization { min-height: 7em; }
    }
}

@media only screen and (max-width: 1100px) {
    .benchmark-cases { padding: 0 0 0.6rem; }
    .case-controls {
        gap: 0.2rem;
        margin-bottom: 0.3rem;
        button { width: 0.8rem; height: 0.8rem; font-size: 0.4rem; }
    }
    .case-track { grid-auto-columns: 80%; column-gap: 0.3rem; padding-bottom: 0.3rem; }
    .case-card {
        padding: 0.3rem;
        .case-image { height: 3rem; }
        h3 { font-size: 0.4rem; margin: 0.3rem 0 0.2rem; }
        .case-description { font-size: 0.32rem; margin-bottom: 0.4rem; }
        .case-organization {
            padding-top: 0.3rem;
            gap: 0.2rem;
            font-size: 0.28rem;
            img { width: 1rem; height: 0.8rem; }
        }
    }
}
</style>
